import React, { useState } from "react"
import PropTypes from "prop-types"
import { Container, Draggable } from "react-smooth-dnd"
import styled from "@emotion/styled"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import { useSnackbar } from "notistack"
import { exchangeSubCategoryIndex } from "services/category"
import { exchangeArrayItem } from "utils/data"
import { StyledCategoryProgramItem } from "./Category.style"

const StyledCategoryReorderSubCategoryDialog = styled(Dialog)`
  .MuiPaper-root {
    width: min(920px, calc(100vw - 30px));
    min-height: 560px;
    border-radius: 18px;
    border: 1px solid var(--cms-border);
    background: var(--cms-dialog-bg);
    box-shadow: 0 24px 52px rgba(15, 23, 42, 0.2);
  }

  .MuiDialogTitle-root {
    padding: 18px 24px 6px;
  }

  .dialog-title {
    margin: 0;
    font-size: 20px;
    font-weight: 800;
    color: var(--cms-text-primary);
    letter-spacing: -0.01em;
  }

  .dialog-subtitle {
    margin: 6px 0 0;
    font-size: 13px;
    color: var(--cms-text-secondary);
    font-weight: 500;
  }

  .MuiDialogContent-root {
    padding: 10px 20px 12px;
  }

  .reorder-list {
    border: 1px dashed var(--cms-border);
    border-radius: 14px;
    padding: 12px;
    background: var(--cms-surface-muted);
    min-height: 320px;
  }

  .empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 140px;
    border-radius: 12px;
    border: 1px dashed var(--cms-border);
    color: var(--cms-text-secondary);
    font-size: 14px;
    font-weight: 600;
  }

  .MuiDialogActions-root {
    padding: 10px 22px 20px;
    gap: 10px;
  }

  .dialog-save-btn {
    text-transform: none;
    font-weight: 700;
    border-radius: 10px;
    padding: 8px 16px;
    box-shadow: 0 12px 24px rgba(29, 78, 216, 0.22);
  }

  .dialog-close-btn {
    text-transform: none;
    font-weight: 700;
    color: var(--cms-text-secondary);
  }

  .smooth-dnd-draggable-wrapper {
    padding: 0 5px;
  }
`

const CategoryReorderSubCategoryDlg = ({ access, reloadCallback }) => {
  const { enqueueSnackbar } = useSnackbar()

  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [categories, setCategories] = useState([])
  const [isChanged, setIsChanged] = useState(false)
  const [categoryId, setCategoryId] = useState(null)

  const openDlg = (subCategories, inputCategoryId) => {
    setCategories(subCategories || [])
    setCategoryId(inputCategoryId)
    setIsChanged(false)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const updateOrder = async () => {
    try {
      setLoading(true)
      const response = await exchangeSubCategoryIndex(
        categories.map((category) => {
          return {
            id: category.id,
            index: category.subCategoryIndex,
          }
        })
      )

      if (response.rtnCode !== 200) {
        enqueueSnackbar("Fail to the update SubCategory order", {
          variant: "warning",
        })
      } else {
        setIsChanged(false)
        enqueueSnackbar("Successfully update SubCategory order", {
          variant: "success",
        })
        reloadCallback(categoryId)
      }
    } catch (e) {
      console.error("error: ", e)
      enqueueSnackbar("Fail to the update SubCategory order", {
        variant: "warning",
      })
    } finally {
      setLoading(false)
    }
  }

  // render program list item
  const renderSubCategoryItem = (category) => {
    return (
      <StyledCategoryProgramItem key={`subcategory-item-${category.id}`}>
        <div className="info">
          <div className="identity">SubCategory ID: {category.id} </div>
          <div className="title">Title: {category.name}</div>
          <div className="desc">Description: {category.description}</div>
        </div>
      </StyledCategoryProgramItem>
    )
  }

  const dlg = (
    <StyledCategoryReorderSubCategoryDialog open={open} onClose={handleClose}>
      <DialogTitle>
        <h3 className="dialog-title">Reorder Subcategories</h3>
        <p className="dialog-subtitle">
          Drag and drop to set the display order inside this category.
        </p>
      </DialogTitle>
      <DialogContent>
        <div className="reorder-list">
          {categories?.length === 0 && (
            <div className="empty-state">No subcategories found.</div>
          )}
          {access.listProgramBySubCategoryAccess ? (
            <Container
              onDrop={async (e) => {
                setIsChanged(true)
                const updateIndexArray = exchangeArrayItem({
                  addedIndex: e.addedIndex,
                  removedIndex: e.removedIndex,
                  indexString: "subCategoryIndex",
                  list: categories,
                })
                setCategories(updateIndexArray)
              }}
            >
              {categories &&
                categories.map((category) => (
                  <Draggable key={category.id}>
                    {renderSubCategoryItem(category)}
                  </Draggable>
                ))}
            </Container>
          ) : (
            categories &&
            categories.map((category) => (
              <Draggable key={category.id}>
                {renderSubCategoryItem(category)}
              </Draggable>
            ))
          )}
        </div>
      </DialogContent>

      <DialogActions>
        {access.listProgramBySubCategoryAccess && (
          <Button
            variant="contained"
            color="primary"
            onClick={updateOrder}
            disabled={!isChanged}
            className="dialog-save-btn"
          >
            Save
          </Button>
        )}
        <Button className="dialog-close-btn" onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </StyledCategoryReorderSubCategoryDialog>
  )

  return [openDlg, dlg, loading]
}

CategoryReorderSubCategoryDlg.propTypes = {
  onSubmit: PropTypes.func,
}

CategoryReorderSubCategoryDlg.defaultProps = {
  onSubmit: null,
}

export default CategoryReorderSubCategoryDlg
